import Cookies from 'js-cookie';
export const HTTP_REQUEST_CONTEXT_HEADER = 'x-udemy-request-context';
export const HTTP_AUTHORIZATION_HEADER = 'authorization';
export const HTTP_ACCEPT_LANGUAGE_HEADER = 'Accept-Language';
export let ErrorPolicy = /*#__PURE__*/function (ErrorPolicy) {
  // If an error is encountered while calling GraphQL, do not return data. Throw the error instead.
  ErrorPolicy["STRICT"] = "strict";
  // If an error is encountered whie calling GraphQL, but partial data was returned as well,
  // do not throw the error. Return the partial data.
  // Still throws the error if no partial data was returned.
  ErrorPolicy["ALLOW_PARTIAL"] = "allowPartial";
  return ErrorPolicy;
}({});
const populateAcceptLanguageHeader = () => {
  var _Cookies$get;
  const cookies = (_Cookies$get = Cookies.get()) !== null && _Cookies$get !== void 0 ? _Cookies$get : {};
  if (cookies.ud_locale) {
    // Convert locale string to Accept-Language standard
    const udLocaleConverted = cookies.ud_locale.split('_').join('-');
    return {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Accept-Language': udLocaleConverted
    };
  }
};
const DEFAULT_FETCHER_CONFIG = {
  getUrl: () => '/api/2024-01/graphql/',
  getHeaders: () => ({}),
  errorPolicy: ErrorPolicy.STRICT
};
const shouldThrowErrorForPolicy = (json, policy) => {
  if (policy === ErrorPolicy.STRICT && json.errors) {
    return true;
  }
  if (policy === ErrorPolicy.ALLOW_PARTIAL && json.errors && !json.data) {
    return true;
  }
  return false;
};
export function createFetcher({
  getUrl = DEFAULT_FETCHER_CONFIG.getUrl,
  getHeaders = DEFAULT_FETCHER_CONFIG.getHeaders,
  errorPolicy = DEFAULT_FETCHER_CONFIG.errorPolicy
}) {
  const fetcher = (query, variables, options) => {
    return async () => {
      /**
       * Because of the graphql-codegen library, we're not able to change the signature of this function. It's hard
       * coded to the codegen itself. So we couldn't change the options parameter to RequestInit.
       * Introduced in this https://github.com/dotansimha/graphql-code-generator/pull/7371
       *
       * Therefore, we needed to implicitly give the signal parameter to options
       */
      let signal;
      if (options && '_signal' in options) {
        signal = options._signal;
        delete options._signal;
      }

      // Resolve request context headers
      const requestContextHeaders = {};
      // By default, pass through authorization header if present
      if (options !== null && options !== void 0 && options[HTTP_AUTHORIZATION_HEADER]) {
        requestContextHeaders[HTTP_AUTHORIZATION_HEADER] = options[HTTP_AUTHORIZATION_HEADER];
      }

      // By default, pass through service request context header if present
      if (options !== null && options !== void 0 && options[HTTP_REQUEST_CONTEXT_HEADER]) {
        requestContextHeaders[HTTP_REQUEST_CONTEXT_HEADER] = options[HTTP_REQUEST_CONTEXT_HEADER];
      }
      const res = await fetch(getUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...populateAcceptLanguageHeader(),
          ...requestContextHeaders,
          // @ts-expect-error - allows us to send an access token cookie when upstream is behind API gateway
          Cookie: options === null || options === void 0 ? void 0 : options.Cookie,
          ...(options !== null && options !== void 0 ? options : {}),
          // User overrides
          ...getHeaders(options !== null && options !== void 0 ? options : {})
        },
        body: JSON.stringify({
          query,
          variables
        }),
        signal
      });

      // Check response status
      if (!res.ok) {
        throw new Error(`Request failed with status code ${res.status} while running query: ${query}`);
      }
      const json = await res.json();
      if (shouldThrowErrorForPolicy(json, errorPolicy)) {
        var _json$errors, _json$errors2;
        const message = ((_json$errors = json.errors) === null || _json$errors === void 0 || (_json$errors = _json$errors[0]) === null || _json$errors === void 0 ? void 0 : _json$errors.message) || 'Error..';
        const code = ((_json$errors2 = json.errors) === null || _json$errors2 === void 0 || (_json$errors2 = _json$errors2[0]) === null || _json$errors2 === void 0 || (_json$errors2 = _json$errors2.extensions) === null || _json$errors2 === void 0 ? void 0 : _json$errors2.code) || 'INTERNAL_SERVER_ERROR';
        throw new Error(message, {
          cause: code
        });
      }
      if (json.data === undefined) {
        // This should never happen; if there's no data, then the GQL API should return some error
        // (see spec: https://spec.graphql.org/June2018/#sec-Errors).
        //
        // No error policy currently allows for the case where there's an error and no data to get to here.
        // Still, we do this to satisfy the TS compiler.
        throw new Error('No data found!');
      }
      return json.data;
    };
  };
  return fetcher;
}
//# sourceMappingURL=create-fetcher.js.map